/**
 * This component is implemented using the structure
 * https://medium.com/@lukaonik/how-to-write-custom-form-controls-in-angular-1285ed00bba0
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import {
  NgbCalendar,
  NgbDateParserFormatter,
  NgbInputDatepicker,
} from "@ng-bootstrap/ng-bootstrap";
import { NgbDate } from "@ng-bootstrap/ng-bootstrap/datepicker/ngb-date";
import * as moment from "moment";
import { NgbCustomParserFormatter } from "../date-picker/ngb-custom-parser-formatter";

export class BaseControlValueAccessor<T> implements ControlValueAccessor {
  @Input() disabled = false;
  @Input() markDisabled: (date: NgbDate, current: { month: number }) => boolean;
  @Output() valueChanged: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Call when value has changed programmatically
   */
  public onChange(newVal: T) {}

  public onTouched(_?: any) {}

  @Input() public value: T;

  /**
   * Model -> View changes
   */
  public writeValue(obj: T): void {
    this.value = obj;
  }

  // setup a listener for changes on the native control
  // and set this value to form control
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

@Component({
  selector: "cur-date-picker",
  templateUrl: "date-picker.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatePickerComponent),
    },
    {
      provide: NgbDateParserFormatter,
      useClass: NgbCustomParserFormatter,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent
  extends BaseControlValueAccessor<string>
  implements OnInit, OnDestroy
{
  @Input() popupPlacement: string[] = null;
  @Input() label: string = null;
  /** Accessible name for the inner date input (not rendered visibly). Bind this
   *  when the visible field label lives outside the component (e.g. a sibling
   *  span) so screen readers still announce the control. */
  @Input() ariaLabel: string = null;
  @Input() useTodayAsDefault: boolean = false;
  @Input() placeholderText: string = "MM/DD/YYYY";
  @Input() maxDateValue: any;
  @Input() minDateValue: any;
  @Input() useUtcDate: boolean = false;
  @Input() autoComplete: string = "on";
  @Input() isReadOnly: boolean = false;
  @Output() calendarIsOpen = new EventEmitter<any>();
  @Output() onBlurOrFocusEvent = new EventEmitter<"blurred" | "focused">();
  private internalDate: NgbDate = null;
  private readonly componentHashed: number = null;
  private currentInstance: NgbInputDatepicker = null;
  public oppenedDatepickerButton = false;
  public firstClick = false;
  private boundOnPageClicked: (event: MouseEvent) => void;

  protected get hasValue() {
    const obj = <any>Object;
    return (
      this.internalDate &&
      obj.values(this.internalDate).every((value) => !isNaN(value))
    );
  }

  constructor(private calendar: NgbCalendar, private cdr: ChangeDetectorRef) {
    super();
    this.componentHashed = this.randomCodeBuilder()();
    this.boundOnPageClicked = this.onPageClicked.bind(this);
    document.addEventListener("click", this.boundOnPageClicked);

    if (this.popupPlacement == null) {
      this.popupPlacement = ["bottom-left"];
    }
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.boundOnPageClicked) {
      document.removeEventListener("click", this.boundOnPageClicked);
    }
  }

  public emitValueChanged(date: NgbDate) {
    if (date) {
      this.onChange(new Date(date.year, date.month - 1, date.day).toString());
    } else {
      this.onChange(null);
    }
  }

  private randomCodeBuilder() {
    let lastRandom = null;
    const min = 1;
    const max = 1000;
    return () => {
      let random = null;
      if (lastRandom === undefined) {
        random = Math.floor(Math.random() * (max - min + 1)) + min;
      } else {
        random = Math.floor(Math.random() * (max - min)) + min;
        if (random >= lastRandom) random += 1;
      }
      lastRandom = random;
      return random;
    };
  }

  public writeValue(obj: string): void {
    super.writeValue(obj);
    if (this.value && this.value !== "") {
      let momentValue;
      if (this.useUtcDate) {
        momentValue = moment.utc(this.value);
      } else {
        momentValue = moment(this.value);
      }
      this.internalDate = new NgbDate(
        momentValue.year(),
        momentValue.month() + 1,
        momentValue.date()
      );
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    } else if (this.useTodayAsDefault) {
      this.internalDate = this.calendar.getToday();
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    } else {
      this.internalDate = null;
    }
    if (this.internalDate) {
      this.valueChanged.emit(
        new Date(
          this.internalDate.year,
          this.internalDate.month - 1,
          this.internalDate.day
        ).toString()
      );
    }
  }

  private onPageClicked(event) {
    let element: HTMLElement = event.target;
    // Stops the check for closing with select month and year
    if (
      element.tagName.toLowerCase() === "select" &&
      element.classList.contains("custom-select")
    ) {
      return;
    }
    // Stops the check for closing with the arrow buttons
    if (
      element.tagName.toLowerCase() === "span" &&
      element.classList.contains("ngb-dp-navigation-chevron")
    ) {
      return;
    }
    if (
      element.tagName.toLowerCase() === "span" &&
      element.classList.contains("button-inner")
    ) {
      element = element.parentElement;
    }
    const uuid = Number.parseFloat(element.dataset.componentUuid);
    if (
      !uuid ||
      (uuid &&
        uuid !== this.componentUUId &&
        this.currentInstance &&
        this.currentInstance.isOpen())
    ) {
      if (this.currentInstance) {
        this.currentInstance.close();
      }
    } else {
      if (this.internalDate == null) {
        this.valueChanged.emit(null);
      }
    }
  }

  public clearDateValue() {
    this.internalDate = null;
    this.emitValueChanged(null);
  }

  protected toggleCalendarDisplay(datePicker: NgbInputDatepicker) {
    this.currentInstance = datePicker;
    datePicker.toggle();
    this.onCalendarIsOpen();
  }

  protected get componentUUId() {
    return this.componentHashed;
  }

  public onFirstClickSection(datePicker: NgbInputDatepicker): void {
    this.firstClick = true;
    this.oppenedDatepickerButton = this.firstClick;
    if (this.firstClick && !datePicker.isOpen()) {
      this.oppenedDatepickerButton = false;
    }
    this.onBlurOrFocusEvent.emit("focused");
    this.onCalendarIsOpen();
  }

  public onSeccondClickSection(): void {
    if (this.firstClick) {
      this.firstClick = false;
      this.oppenedDatepickerButton = this.firstClick;
    }
    this.onBlurOrFocusEvent.emit("focused");
    this.onCalendarIsOpen();
  }

  public onLeaveClickSection(): void {
    this.oppenedDatepickerButton = false;
    this.onBlurOrFocusEvent.emit("blurred");
    this.onCalendarIsOpen();
  }

  public onCalendarIsOpen(): void {
    if (this.currentInstance) {
      this.calendarIsOpen.emit(this.currentInstance.isOpen());
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/common/date-picker/date-picker.ts